import { useState, useEffect, useCallback } from 'react';
import api from '../../../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { toast } from 'sonner';
import { fmtD } from './planningUtils';

export function AssignConfirmDialog({ joNumber, open, onOpenChange, onDone }) {
  const [detail, setDetail] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [priority, setPriority] = useState('normal');
  const [team, setTeam] = useState('');
  const [force, setForce] = useState(false);
  const [riskMsg, setRiskMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/admin/production-planning/allocations/${joNumber}`);
      setDetail(data);
    } catch (e) { toast.error('Failed to load allocation details'); }
  }, [joNumber]);

  useEffect(() => {
    if (open && joNumber) { setRemarks(''); setPriority('normal'); setTeam(''); setForce(false); setRiskMsg(''); load(); }
  }, [open, joNumber, load]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/api/admin/production-planning/assign/${joNumber}`, { remarks, priority, team, force });
      toast.success(`JO ${joNumber} assigned to production`);
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      const detailMsg = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detailMsg === 'string' && detailMsg.includes('DELIVERY RISK')) {
        setRiskMsg(detailMsg);
      } else {
        toast.error(typeof detailMsg === 'string' ? detailMsg : 'Assignment failed');
      }
    }
    setSaving(false);
  };

  const jo = detail?.jo;
  const allocs = detail?.allocations || [];
  const buffer = jo?.delivery_date && jo?.production_end
    ? Math.round((new Date(jo.delivery_date) - new Date(jo.production_end)) / 86400000) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="assign-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Assign to Production — {joNumber}</DialogTitle>
        </DialogHeader>
        {jo && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-2">
              {[['Customer', jo.customer_name], ['Total Qty', jo.total_qty], ['Delivery Date', fmtD(jo.delivery_date)],
                ['Production Start', fmtD(jo.production_start)], ['Production End', fmtD(jo.production_end)],
                ['Buffer Before Delivery', buffer !== null ? `${buffer} day(s)` : '—']].map(([k, v]) => (
                <div key={k} className="p-2 rounded-sm border border-zinc-200 bg-zinc-50">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
                  <div className="font-bold">{v}</div>
                </div>
              ))}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Day-wise Allocation</div>
              <div className="flex flex-wrap gap-1">
                {allocs.map((a) => (
                  <span key={a.date} className={`px-2 py-0.5 rounded text-[11px] font-mono border ${a.allocation_type === 'tentative' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-blue-50 border-blue-300 text-blue-800'}`}>
                    {fmtD(a.date)} × {a.qty}
                  </span>
                ))}
                {allocs.length === 0 && <span className="text-zinc-400 text-xs">No allocations — will auto-allocate on assign</span>}
              </div>
            </div>
            {jo.shortfall > 0 && (
              <div className="p-2 rounded-sm bg-red-50 border border-red-300 text-red-700 text-xs font-bold">
                DELIVERY RISK: {jo.shortfall} of {jo.total_qty} units could not be scheduled before delivery.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="assign-priority"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Production Team (optional)</Label>
                <Input value={team} onChange={(e) => setTeam(e.target.value)} data-testid="assign-team" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Remarks</Label>
              <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="assign-remarks" placeholder="Optional remarks for production" />
            </div>
            {riskMsg && (
              <div className="p-2 rounded-sm bg-red-50 border border-red-300 text-xs space-y-2">
                <div className="text-red-700 font-bold">{riskMsg}</div>
                <label className="flex items-center gap-2 text-red-800">
                  <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="assign-force-checkbox" />
                  I understand the delivery risk — proceed anyway (logged as override)
                </label>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={saving || (!!riskMsg && !force)}
                onClick={submit} data-testid="assign-confirm-btn">
                {saving ? 'Assigning…' : 'Confirm & Assign'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
